import type { IBillingRepository } from '@yawi/domain';
import type {
    AddBankPaymentMethodDto,
    AddCardPaymentMethodDto,
    AddPaymentMethodDto,
    ChangePlanResponse,
    CreatePaymentIntentResponse,
    FeatureAccess,
    ModuleAccess,
    ModulesChangeResponse,
    PayInvoiceResponse,
    TenantBillingCycle,
    TenantBillingInvoice,
    TenantBillingPlan,
    TenantBillingSubscription,
    TenantBillingSummary,
    TenantBillingUsageHistory,
    TenantInvoiceStatusType,
    TenantPaymentMethod,
    TenantPendingInvoice,
    TenantPremiumModule,
    TenantUsageSummary,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiBillingRepository implements IBillingRepository {
  private readonly basePath = 'v1/billing';

  // ============ SUMMARY ============
  async getBillingSummary(): Promise<TenantBillingSummary> {
    const client = getApiClient();
    const response = await client.get<TenantBillingSummary>(`${this.basePath}/summary`);
    return response;
  }

  // ============ SUBSCRIPTION ============
  async getSubscription(): Promise<TenantBillingSubscription> {
    const client = getApiClient();
    const response = await client.get<TenantBillingSubscription>(`${this.basePath}/subscription`);
    return response;
  }

  async changePlan(planId: string, billingCycle?: TenantBillingCycle): Promise<ChangePlanResponse> {
    const client = getApiClient();
    const response = await client.put<ChangePlanResponse>(
      `${this.basePath}/subscription/plan`,
      { planId, billingCycle }
    );
    return response;
  }

  // ============ USAGE ============
  async getCurrentUsage(period?: string): Promise<TenantUsageSummary> {
    const client = getApiClient();
    const queryString = period ? `?period=${period}` : '';
    const response = await client.get<TenantUsageSummary>(`${this.basePath}/usage${queryString}`);
    return response;
  }

  async getUsageHistory(limit?: number): Promise<TenantBillingUsageHistory[]> {
    const client = getApiClient();
    const queryString = limit ? `?limit=${limit}` : '';
    const response = await client.get<TenantBillingUsageHistory[]>(`${this.basePath}/usage/history${queryString}`);
    return response;
  }

  // ============ PLANS ============
  async getAvailablePlans(): Promise<TenantBillingPlan[]> {
    const client = getApiClient();
    const response = await client.get<TenantBillingPlan[]>(`${this.basePath}/plans`);
    return response;
  }

  // ============ MODULES ============
  async getAvailableModules(): Promise<TenantPremiumModule[]> {
    const client = getApiClient();
    const response = await client.get<TenantPremiumModule[]>(`${this.basePath}/modules`);
    return response;
  }

  async addModules(moduleCodes: string[]): Promise<ModulesChangeResponse> {
    const client = getApiClient();
    const response = await client.post<ModulesChangeResponse>(
      `${this.basePath}/subscription/modules`,
      { moduleCodes }
    );
    return response;
  }

  async removeModules(moduleCodes: string[]): Promise<ModulesChangeResponse> {
    const client = getApiClient();
    const response = await client.delete<ModulesChangeResponse>(
      `${this.basePath}/subscription/modules`,
      { body: { moduleCodes } }
    );
    return response;
  }

  // ============ INVOICES ============
  async getInvoices(status?: TenantInvoiceStatusType): Promise<TenantBillingInvoice[]> {
    const client = getApiClient();
    const queryString = status ? `?status=${status}` : '';
    const response = await client.get<TenantBillingInvoice[]>(`${this.basePath}/invoices${queryString}`);
    return response;
  }

  async getInvoiceById(invoiceId: string): Promise<TenantBillingInvoice> {
    const client = getApiClient();
    const response = await client.get<TenantBillingInvoice>(`${this.basePath}/invoices/${invoiceId}`);
    return response;
  }

  async getPendingInvoices(): Promise<TenantPendingInvoice[]> {
    const client = getApiClient();
    const response = await client.get<TenantPendingInvoice[]>(`${this.basePath}/invoices/pending`);
    return response;
  }

  async payInvoice(invoiceId: string, paymentMethodId: string): Promise<PayInvoiceResponse> {
    const client = getApiClient();
    const response = await client.post<PayInvoiceResponse>(
      `${this.basePath}/invoices/${invoiceId}/pay`,
      { paymentMethodId }
    );
    return response;
  }

  async createPaymentIntent(invoiceId: string): Promise<CreatePaymentIntentResponse> {
    const client = getApiClient();
    const response = await client.post<CreatePaymentIntentResponse>(
      `${this.basePath}/invoices/${invoiceId}/create-payment-intent`
    );
    return response;
  }

  // ============ PAYMENT METHODS ============
  async getPaymentMethods(): Promise<TenantPaymentMethod[]> {
    const client = getApiClient();
    const response = await client.get<TenantPaymentMethod[]>(`${this.basePath}/payment-methods`);
    return response;
  }

  /**
   * @deprecated Use addCardPaymentMethod or addBankPaymentMethod instead
   */
  async addPaymentMethod(dto: AddPaymentMethodDto): Promise<TenantPaymentMethod> {
    const client = getApiClient();
    const response = await client.post<TenantPaymentMethod>(
      `${this.basePath}/payment-methods/card`,
      { stripePaymentMethodId: dto.stripePaymentMethodId }
    );
    return response;
  }

  async addCardPaymentMethod(dto: AddCardPaymentMethodDto): Promise<TenantPaymentMethod> {
    const client = getApiClient();
    const response = await client.post<TenantPaymentMethod>(
      `${this.basePath}/payment-methods/card`,
      dto
    );
    return response;
  }

  async addBankPaymentMethod(dto: AddBankPaymentMethodDto): Promise<TenantPaymentMethod> {
    const client = getApiClient();
    const response = await client.post<TenantPaymentMethod>(
      `${this.basePath}/payment-methods/bank`,
      dto
    );
    return response;
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    const client = getApiClient();
    await client.put(`${this.basePath}/payment-methods/${paymentMethodId}/default`);
  }

  async deletePaymentMethod(paymentMethodId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.basePath}/payment-methods/${paymentMethodId}`);
  }

  // ============ SUBSCRIPTION ACTIONS ============
  async cancelSubscription(): Promise<TenantBillingSubscription> {
    const client = getApiClient();
    const response = await client.post<TenantBillingSubscription>(
      `${this.basePath}/subscription/cancel`
    );
    return response;
  }

  // ============ ACCESS CHECKS ============
  async checkFeatureAccess(featureKey: string): Promise<FeatureAccess> {
    const client = getApiClient();
    const response = await client.get<FeatureAccess>(
      `${this.basePath}/features/${featureKey}`
    );
    return response;
  }

  async checkModuleAccess(moduleCode: string): Promise<ModuleAccess> {
    const client = getApiClient();
    const response = await client.get<ModuleAccess>(
      `${this.basePath}/modules/${moduleCode}/access`
    );
    return response;
  }
}
